import styled from '@emotion/styled';
import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';
import { Fragment, useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { ApplicationContext } from '~/shared/context/context';
import type { JobCluster } from '~/shared/data-contract/job-cluster';
import type { LanguageCodeForJSA } from '~/shared/internationalization';
import type { TranslationKey } from '~/shared/internationalization/translation';
import { ShakeAnimation } from '~/ui/animations/shake/ShakeAnimation';
import { useShakeOnClickOutside } from '~/ui/animations/shake/use-shake-on-click-outside';
import { Button } from '~/ui/core/button/Button';
import { ConfirmationDialog } from '~/ui/core/confirmation-dialog/ConfirmationDialog';
import { ScrollArea } from '~/ui/core/scroll-area/ScrollArea';
import { Textarea } from '~/ui/core/textarea/Textarea';
import { Tile, TileContent } from '~/ui/core/tile/Tile';
import { SidepanelHeader } from '~/ui/domain/Sidepanel/components/SidepanelHeader';
import { theme } from '~/ui/theme/cobrainer-theme';

import { useTileNavigation } from '../../url-tiles';
import { useJobClusterDetails } from '../api/use-job-cluster-details';
import { usePatchJobCluster } from '../api/use-patch-job-cluster';
import { Action, CTAWrapper } from '../components/Action';
import { Backdrop, ContentContainer, StyledStickyHeader, Wrapper } from '../components/Common';
import { ErrorContent } from '../components/ErrorContent';
import { SidepanelTitle } from '../components/SidepanelTitle';
import { DescriptionSectionSkeleton, TitleSkeleton } from '../components/SkeletonLoaders';
import { Spinner } from '../components/Spinner';
import { useDiscardConfirmation } from '../hooks/use-discard-confirmation';
import { useSetupLanguage } from '../hooks/use-language';
import { JSA_PRIMARY_DETAIL_TILE_SLOT_KEY } from '../url-tile-payloads';
import { JobClusterOptionsMenu } from './OptionsMenu';
import type { JobClusterForm } from './types';

const StyledTextarea = styled(Textarea)(({ theme }) => ({
    fontSize: theme.typography.body.S.regular.fontSize,
    lineHeight: '21px',
    marginBottom: theme.spacing.xxl,
    marginTop: theme.spacing.xxl,
    minHeight: '400px',
}));

const DescriptionSection: FunctionComponent<{
    readonly description?: string;
    readonly isEditing: boolean;
    // biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration
    readonly register: any;
}> = ({ description, isEditing, register }) => {
    if (isEditing) {
        return <StyledTextarea {...register} aria-label="description" />;
    }

    return <p>{description}</p>;
};

// biome-ignore lint/suspicious/noRedeclare: suppressed due to migration
const JobClusterForm: FunctionComponent<{
    readonly activeLanguage: LanguageCodeForJSA;
    readonly activeLanguageIndex: number;
    readonly cluster: JobCluster;
    readonly isEditing: boolean;
    readonly jobArchitectureId: string;
    readonly setIsEditing: (isEditing: boolean) => void;
}> = ({ activeLanguage, activeLanguageIndex, cluster, isEditing, setIsEditing }) => {
    const formMethods = useForm<JobClusterForm>({
        defaultValues: {
            translations: cluster.translations,
        },
    });
    const { handleSubmit, register, reset, setValue } = formMethods;

    const { translate } = useContext(ApplicationContext);
    const [isSaving, setIsSaving] = useState(false);
    const { mutateAsync: updateJobCluster } = usePatchJobCluster();
    const activeTranslation = cluster.translations.find((translation) => translation.language === activeLanguage);

    const { handleCancel, handleCancelDiscard, handleConfirmDiscard, isDiscardDialogOpen } = useDiscardConfirmation({
        formMethods,
        isEditing,
        onClose: () => setIsEditing(false),
        setIsEditing,
    });

    useEffect(() => {
        if (isEditing === false) {
            reset();
            setValue('translations', cluster.translations);
        }
    }, [reset, isEditing, setValue, cluster.translations]);

    const handleSave = async (jobClusterForm: JobClusterForm): Promise<void> => {
        try {
            setIsSaving(true);
            await updateJobCluster({
                jobCluster: {
                    ...cluster,
                    translations: jobClusterForm.translations.filter(
                        (translation) => translation.language === activeLanguage,
                    ),
                },
            });
        } finally {
            setIsSaving(false);
            setIsEditing(false);
            reset();
        }
    };

    return (
        <Fragment>
            <ConfirmationDialog
                cancelButtonLabel={translate('common.cancel')}
                confirmButtonLabel={translate('common.confirm')}
                confirmButtonVariant="default"
                description={translate('dialogs.discard.content')}
                open={isDiscardDialogOpen}
                title={translate('dialogs.discard.title')}
                onCancel={handleCancelDiscard}
                onConfirm={handleConfirmDiscard}
            />
            <SidepanelTitle
                key={`title-${activeLanguageIndex}`}
                isEditing={isEditing}
                register={register(`translations.${activeLanguageIndex}.title`)}
                title={activeTranslation?.title ?? ''}
            />
            <Action style={{ justifyContent: 'flex-end' }}>
                {!isEditing ? (
                    <CTAWrapper>
                        <Button
                            disabled={!cluster.actions.canEdit}
                            variant="tertiary"
                            onClick={() => setIsEditing(true)}
                        >
                            {translate('common.edit')}
                        </Button>
                    </CTAWrapper>
                ) : (
                    <CTAWrapper>
                        <Button variant="tertiary" onClick={handleCancel}>
                            {translate('common.cancel')}
                        </Button>
                        <Button variant="primary" onClick={handleSubmit(handleSave)}>
                            {isSaving ? <Spinner /> : translate('common.save')}
                        </Button>
                    </CTAWrapper>
                )}
            </Action>
            <DescriptionSection
                key={`description-${activeLanguageIndex}`}
                description={activeTranslation?.description}
                isEditing={isEditing}
                register={register(`translations.${activeLanguageIndex}.description`)}
            />
        </Fragment>
    );
};

export const JobClusterDetails: FunctionComponent<{
    readonly architectureId: string;
    readonly clusterId: string;
    readonly familyId: string;
    readonly mode: 'edit' | 'view';
    readonly onClose: () => void;
}> = ({ architectureId, clusterId, familyId, mode, onClose }) => {
    const { data: cluster, error, isError, isLoading } = useJobClusterDetails(architectureId, familyId, clusterId);

    const { activeLanguage, activeLanguageIndex, availableLanguages, setActiveLanguage } = useSetupLanguage(
        undefined,
        cluster,
    );
    const { translate } = useContext(ApplicationContext);
    const [isEditing, setIsEditing] = useState(mode === 'edit');
    const { isShaking, shake } = useShakeOnClickOutside();

    const tileNavigation = useTileNavigation(`${JSA_PRIMARY_DETAIL_TILE_SLOT_KEY}.${architectureId}.${clusterId}`);

    const header = (
        <StyledStickyHeader>
            <SidepanelHeader
                actions={
                    cluster === undefined ? undefined : <JobClusterOptionsMenu cluster={cluster} onClose={onClose} />
                }
                activeLanguage={
                    activeLanguage === undefined
                        ? undefined
                        : translate(`common.languages.${activeLanguage}` as TranslationKey)
                }
                isGenerating={cluster?.generationInfo?.inProgress ?? false}
                isLanguagePickerDisabled={isEditing}
                languages={availableLanguages}
                setActiveLanguage={setActiveLanguage}
                onClose={onClose}
                onNext={tileNavigation?.onNext}
                onPrevious={tileNavigation?.onPrevious}
            />
        </StyledStickyHeader>
    );

    if (isLoading) {
        return (
            <Tile data-testid="job-cluster-details-loading">
                <ScrollArea>
                    <TileContent>
                        <ContentContainer>
                            <Wrapper>
                                {header}
                                <div style={{ marginBottom: '-1.8rem', marginTop: '3.3rem' }}>
                                    <TitleSkeleton />
                                </div>
                                <Action>
                                    <div style={{ height: '2.2rem' }} />
                                </Action>
                                <DescriptionSectionSkeleton />
                            </Wrapper>
                        </ContentContainer>
                    </TileContent>
                </ScrollArea>
            </Tile>
        );
    }

    if (isError || is.undefined(cluster)) {
        return (
            <Tile>
                <ScrollArea>
                    <TileContent>
                        <ContentContainer>
                            <ErrorContent error={error} onClose={onClose} />
                        </ContentContainer>
                    </TileContent>
                </ScrollArea>
            </Tile>
        );
    }

    return (
        <Fragment>
            {isEditing && (
                <Backdrop
                    animate={{ opacity: '0.4' }}
                    initial={{ backgroundColor: theme.colors.gray[200], opacity: '0' }}
                    onClick={shake}
                />
            )}
            <ShakeAnimation isShaking={isShaking}>
                <Tile data-testid="job-cluster-details">
                    <ScrollArea>
                        <TileContent>
                            <ContentContainer>
                                <Wrapper>
                                    {header}
                                    <JobClusterForm
                                        activeLanguage={activeLanguage}
                                        activeLanguageIndex={activeLanguageIndex}
                                        cluster={cluster}
                                        isEditing={isEditing}
                                        jobArchitectureId={architectureId}
                                        setIsEditing={setIsEditing}
                                    />
                                </Wrapper>
                            </ContentContainer>
                        </TileContent>
                    </ScrollArea>
                </Tile>
            </ShakeAnimation>
        </Fragment>
    );
};
